import { useState, useEffect } from 'react'

type Scope = 'core' | 'vc' | 'lc'
type ScopeFilter = 'all' | 'core' | 'vc' | 'lc' | 'vc-rel' | 'lc-rel'
type StatusFilter = 'all' | 'official' | 'experimental'

interface Metric {
  id: string
  label: string
  formula: string
  type: 'ratio' | 'currency' | 'percent' | 'count' | 'duration'
  oficial: boolean
  scope: Scope
  author?: string
}

// ROI e ticket ('roi', 'roi_bay', 'tm_with', 'tm_without') são espelho
// read-only do backend (analytics-experiments/src/backend/config/roi-methodologies.config.ts
// e benchmark-metrics.config.ts) — mesma convenção do health-checks.config.ts.
// Se a fórmula ou o status oficial mudar lá, atualize aqui também.
const METRICS: Metric[] = [
  // Core — shared across all products
  { id: 'roi',        label: 'ROI Widde',                        formula: '(receita_influenciada - custo_proporcional) / custo_proporcional', type: 'ratio',    oficial: true,  scope: 'core' },
  { id: 'rev_inf',    label: 'Receita Influenciada',              formula: 'sum(orders.where(widde_touch).total)',                type: 'currency', oficial: true,  scope: 'core' },
  { id: 'rev_direct', label: 'Receita Direta',                    formula: 'sum(orders.where(widde_attribution=last).total)',     type: 'currency', oficial: true,  scope: 'core' },
  { id: 'pct_inf',    label: '% Pedidos com Widde',               formula: 'count(orders.influenced) / count(orders)',           type: 'percent',  oficial: true,  scope: 'core' },
  { id: 'tm_with',    label: 'Ticket Médio c/ Widde',             formula: 'avg(orders.influenced.total)',                       type: 'currency', oficial: true,  scope: 'core' },
  { id: 'tm_without', label: 'Ticket Médio s/ Widde',             formula: 'avg(orders.organic.total)',                          type: 'currency', oficial: true,  scope: 'core' },
  { id: 'roi_bay',    label: 'ROI Bayesiano',                     formula: 'shrinkage(roi, prior=segment_median, n=orders)',      type: 'ratio',    oficial: true,  scope: 'core', author: 'ubirajara@widde.io' },
  // Video Commerce
  { id: 'video_views', label: 'Vídeos assistidos',                formula: 'count(video.play.unique_session)',                   type: 'count',    oficial: true,  scope: 'vc' },
  { id: 'completion',  label: 'Taxa de conclusão',                formula: 'count(video.complete) / count(video.play)',          type: 'percent',  oficial: true,  scope: 'vc' },
  { id: 'cta_ctr',     label: 'CTR no CTA do vídeo',              formula: 'count(video.cta) / count(video.play)',               type: 'percent',  oficial: true,  scope: 'vc' },
  { id: 'avg_watch',   label: 'Tempo médio assistido',            formula: 'median(video.watch_time)',                           type: 'duration', oficial: true,  scope: 'vc' },
  { id: 'lift_video',  label: 'Lift conv. c/ vídeo',              formula: '(conv_video - conv_organic) / conv_organic',         type: 'percent',  oficial: false, scope: 'vc', author: 'pedro@widde.io' },
  // Live Commerce
  { id: 'concurrent',   label: 'Pico de simultâneos',            formula: 'max(live.viewers_concurrent)',                       type: 'count',    oficial: true,  scope: 'lc' },
  { id: 'avg_watch_lc', label: 'Tempo médio na live',             formula: 'median(live.watch_time)',                            type: 'duration', oficial: true,  scope: 'lc' },
  { id: 'reactions',    label: 'Reações por live',                formula: 'sum(live.reaction)',                                 type: 'count',    oficial: true,  scope: 'lc' },
  { id: 'chat_msgs',    label: 'Mensagens no chat',               formula: 'count(live.chat)',                                   type: 'count',    oficial: true,  scope: 'lc' },
  { id: 'conv_live',    label: 'Conversão durante live',          formula: 'count(order.during_live) / count(live.viewer)',      type: 'percent',  oficial: true,  scope: 'lc' },
  { id: 'lift_live',    label: 'Lift de retorno na live',         formula: '(ret_live - ret_organic) / ret_organic',             type: 'percent',  oficial: false, scope: 'lc', author: 'ubirajara@widde.io' },
]

const SCOPE_META: Record<Scope, { label: string; short: string; borderColor: string; bg: string; color: string; desc: string }> = {
  core: { label: 'Núcleo', short: 'Núcleo', borderColor: 'var(--ink-400)', bg: 'var(--ink-100)', color: 'var(--ink-700)', desc: 'Compartilhada entre todos os produtos' },
  vc:   { label: 'Video Commerce', short: 'VC', borderColor: 'var(--brand-500)', bg: 'var(--brand-500)', color: '#fff', desc: 'Exclusiva do Video Commerce' },
  lc:   { label: 'Live Commerce', short: 'LC', borderColor: 'var(--accent-coral)', bg: 'var(--accent-coral)', color: '#fff', desc: 'Exclusiva do Live Commerce' },
}

const TYPE_LABELS: Record<Metric['type'], string> = {
  ratio: 'Razão',
  currency: 'Moeda',
  percent: 'Percentual',
  count: 'Contagem',
  duration: 'Duração',
}

interface MetricsCatalogPageProps {
  userMode: 'analyst' | 'scientist'
  productMode: 'stories' | 'live'
}

export function MetricsCatalogPage({ userMode, productMode }: MetricsCatalogPageProps) {
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [q, setQ] = useState('')

  // Auto-set scope when product changes
  useEffect(() => {
    if (productMode === 'stories') setScopeFilter('vc-rel')
    else if (productMode === 'live') setScopeFilter('lc-rel')
    else setScopeFilter('all')
  }, [productMode])

  const isScientist = userMode === 'scientist'

  const counts = {
    all: METRICS.length,
    core: METRICS.filter(m => m.scope === 'core').length,
    vc: METRICS.filter(m => m.scope === 'vc').length,
    lc: METRICS.filter(m => m.scope === 'lc').length,
  }

  const filtered = METRICS.filter(m => {
    if (scopeFilter === 'core' && m.scope !== 'core') return false
    if (scopeFilter === 'vc' && m.scope !== 'vc') return false
    if (scopeFilter === 'lc' && m.scope !== 'lc') return false
    if (scopeFilter === 'vc-rel' && m.scope !== 'core' && m.scope !== 'vc') return false
    if (scopeFilter === 'lc-rel' && m.scope !== 'core' && m.scope !== 'lc') return false
    if (statusFilter === 'official' && !m.oficial) return false
    if (statusFilter === 'experimental' && m.oficial) return false
    if (q && !m.label.toLowerCase().includes(q.toLowerCase()) && !m.id.includes(q.toLowerCase())) return false
    return true
  })

  const scopeTabs: Array<{ k: ScopeFilter; l: string; n: number; dotColor?: string }> = [
    { k: 'all',    l: 'Todas',        n: counts.all },
    { k: 'core',   l: 'Núcleo',       n: counts.core,              dotColor: 'var(--ink-500)' },
    { k: 'vc-rel', l: 'VC + Núcleo',  n: counts.core + counts.vc,  dotColor: 'var(--brand-500)' },
    { k: 'lc-rel', l: 'LC + Núcleo',  n: counts.core + counts.lc,  dotColor: 'var(--accent-coral)' },
    { k: 'vc',     l: 'Só VC',        n: counts.vc,                dotColor: 'var(--brand-500)' },
    { k: 'lc',     l: 'Só LC',        n: counts.lc,                dotColor: 'var(--accent-coral)' },
  ]

  const statusTabs: Array<{ k: StatusFilter; l: string }> = [
    { k: 'all',          l: 'Todos status' },
    { k: 'official',     l: 'Oficial' },
    { k: 'experimental', l: 'Experimental' },
  ]

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Lab</div>
          <h1 style={{ margin: '4px 0 6px', fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>Catálogo de Métricas</h1>
          <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>
            {counts.all} métricas · {counts.core} compartilhadas · {counts.vc} de Video Commerce · {counts.lc} de Live Commerce
          </div>
        </div>
        {isScientist && (
          <button className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Nova métrica
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="card" style={{ marginBottom: 14, padding: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-400)', pointerEvents: 'none' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
          </span>
          <input
            className="input"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar por nome ou ID…"
            style={{ paddingLeft: 30, height: 34, fontSize: 12 }}
          />
        </div>

        {/* Scope tabs */}
        <div style={{ display: 'flex', gap: 3, background: 'var(--ink-100)', padding: 3, borderRadius: 999, flexWrap: 'wrap' }}>
          {scopeTabs.map(t => (
            <button
              key={t.k}
              onClick={() => setScopeFilter(t.k)}
              style={{
                padding: '5px 10px', borderRadius: 999, border: 'none', cursor: 'pointer',
                background: scopeFilter === t.k ? 'var(--paper)' : 'transparent',
                fontSize: 11, fontWeight: 600, color: 'var(--ink-700)',
                boxShadow: scopeFilter === t.k ? 'var(--shadow-sm)' : 'none',
                fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5,
                transition: 'background 0.1s, box-shadow 0.1s',
              }}
            >
              {t.dotColor && (
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.dotColor, flexShrink: 0 }} />
              )}
              {t.l}
              <span style={{ fontSize: 10, color: 'var(--ink-500)' }}>{t.n}</span>
            </button>
          ))}
        </div>

        {/* Status tabs */}
        <div style={{ display: 'flex', gap: 3, background: 'var(--ink-100)', padding: 3, borderRadius: 999 }}>
          {statusTabs.map(t => (
            <button
              key={t.k}
              onClick={() => setStatusFilter(t.k)}
              style={{
                padding: '5px 10px', borderRadius: 999, border: 'none', cursor: 'pointer',
                background: statusFilter === t.k ? 'var(--paper)' : 'transparent',
                fontSize: 11, fontWeight: 600, color: 'var(--ink-700)',
                boxShadow: statusFilter === t.k ? 'var(--shadow-sm)' : 'none',
                fontFamily: 'inherit',
                transition: 'background 0.1s, box-shadow 0.1s',
              }}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* Metric cards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {filtered.map(m => {
          const sm = SCOPE_META[m.scope]
          return (
            <MetricCard
              key={m.id}
              metric={m}
              scopeMeta={sm}
              isScientist={isScientist}
            />
          )
        })}
        {filtered.length === 0 && (
          <div
            className="card"
            style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 48, color: 'var(--ink-500)' }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, marginBottom: 10 }}><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Nenhuma métrica encontrada</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Tente outros filtros ou termos de busca.</div>
          </div>
        )}
      </div>
    </div>
  )
}

interface MetricCardProps {
  metric: Metric
  scopeMeta: typeof SCOPE_META[Scope]
  isScientist: boolean
}

function MetricCard({ metric, scopeMeta, isScientist }: MetricCardProps) {
  return (
    <div
      className="card"
      style={{ padding: 0, overflow: 'hidden', borderLeft: `3px solid ${scopeMeta.borderColor}` }}
    >
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Name row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-900)' }}>{metric.label}</span>
              <span
                style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  background: scopeMeta.bg, color: scopeMeta.color,
                  textTransform: 'uppercase', letterSpacing: '.04em', flexShrink: 0,
                }}
                title={scopeMeta.desc}
              >
                {scopeMeta.short}
              </span>
              {metric.oficial ? (
                <span className="pill pill-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Oficial
                </span>
              ) : (
                <span className="pill pill-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H5.5A2.5 2.5 0 0 0 3 5.5v0A2.5 2.5 0 0 0 5.5 8H9"/><path d="M15 3h3.5A2.5 2.5 0 0 1 21 5.5v0A2.5 2.5 0 0 1 18.5 8H15"/><path d="M9 3v18"/><path d="M15 3v18"/><path d="M9 21H5.5A2.5 2.5 0 0 1 3 18.5v0A2.5 2.5 0 0 1 5.5 16H9"/><path d="M15 21h3.5A2.5 2.5 0 0 0 21 18.5v0A2.5 2.5 0 0 0 18.5 16H15"/></svg>
                  Experimental
                </span>
              )}
            </div>

            {/* Metric ID */}
            <div style={{ fontSize: 11, color: 'var(--ink-500)', fontFamily: 'var(--font-mono, monospace)', marginBottom: 8 }}>
              {metric.id}
            </div>

            {/* Formula */}
            <div
              style={{
                fontFamily: 'var(--font-mono, monospace)', fontSize: 11, color: 'var(--ink-700)',
                background: 'var(--ink-50)', padding: '6px 8px', borderRadius: 6,
                border: '1px solid var(--ink-100)', marginBottom: 6,
                overflowX: 'auto', whiteSpace: 'nowrap',
              }}
            >
              {metric.formula}
            </div>

            {/* Type + author */}
            <div style={{ fontSize: 11, color: 'var(--ink-500)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Tipo: <strong style={{ color: 'var(--ink-700)' }}>{TYPE_LABELS[metric.type]}</strong></span>
              {metric.author && (
                <>
                  <span style={{ color: 'var(--ink-300)' }}>·</span>
                  <span>por {metric.author}</span>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button className="btn btn-icon btn-ghost" title="Visualizar">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
            {isScientist && (
              <button className="btn btn-icon btn-ghost" title="Editar">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
